import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { LoadingSpinner } from './components/ui/LoadingSpinner'

// Layout Components
import { DashboardLayout } from './components/layout/DashboardLayout'
import { AuthLayout } from './components/layout/AuthLayout'

// Page Components
import { LoginPage } from './pages/auth/LoginPage'
import { RegisterPage } from './pages/auth/RegisterPage'
import { OnboardingWizard } from './components/onboarding/OnboardingWizard'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { DashboardPage } from './pages/dashboard/DashboardPage'
import { BusinessProfilePage } from './pages/business/BusinessProfilePage'
import { ScoreHistoryPage } from './pages/score/ScoreHistoryPage'
import { RecommendationsPage } from './pages/recommendations/RecommendationsPage'
import { AlertsPage } from './pages/alerts/AlertsPage'
import { BadgesPage } from './pages/badges/BadgesPage'
import { SettingsPage } from './pages/settings/SettingsPage'
import { NotFoundPage } from './pages/NotFoundPage'

// Lender Portal Pages
import { LenderDashboardPage } from './pages/lender/LenderDashboardPage'
import { BusinessSearchPage } from './pages/lender/BusinessSearchPage'
import { BusinessDetailPage } from './pages/lender/BusinessDetailPage'
import { ScoreComparisonPage } from './pages/lender/ScoreComparisonPage'
import { RiskAssessmentPage } from './pages/lender/RiskAssessmentPage'
import { AlertSubscriptionPage } from './pages/lender/AlertSubscriptionPage'
import { PortfolioPage } from './pages/lender/PortfolioPage'

// Admin Portal
import { AdminRouteGuard } from './components/admin/AdminRouteGuard'
import { AdminLayout } from './components/layout/AdminLayout'
import {
  AdminDashboard,
  AdminUsers,
  AdminBusinesses,
  AdminImpersonation,
  AdminSystem,
  AdminLenders,
  AdminAccessControl,
  AdminAuditLog,
  AdminHealth,
  AdminSubscriptions,
  AdminAPIKeys,
} from './pages/admin'

function App() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Role-based redirect for the root route
  const getRoleDefaultRoute = () => {
    if (!user) return '/auth/login'
    switch (user.role) {
      case 'admin':
        return '/admin'
      case 'lender':
        return '/lender'
      default:
        return '/dashboard'
    }
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="register-onboarding" element={<OnboardingWizard />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route index element={<Navigate to="/auth/login" replace />} />
      </Route>

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          user ? (
            <DashboardLayout />
          ) : (
            <Navigate to="/auth/login" replace />
          )
        }
      >
        {/* Business Dashboard Routes */}
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="business" element={<BusinessProfilePage />} />
        <Route path="score-history" element={<ScoreHistoryPage />} />
        <Route path="recommendations" element={<RecommendationsPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="badges" element={<BadgesPage />} />
        <Route path="settings" element={<SettingsPage />} />

        {/* Lender Portal Routes */}
        <Route path="lender">
          <Route index element={<LenderDashboardPage />} />
          <Route path="dashboard" element={<LenderDashboardPage />} />
          <Route path="search" element={<BusinessSearchPage />} />
          <Route path="business/:businessId" element={<BusinessDetailPage />} />
          <Route path="compare" element={<ScoreComparisonPage />} />
          <Route path="risk-assessment" element={<RiskAssessmentPage />} />
          <Route path="risk-assessment/:businessId" element={<RiskAssessmentPage />} />
          <Route path="alerts" element={<AlertSubscriptionPage />} />
          <Route path="portfolio" element={<PortfolioPage />} />
        </Route>
      </Route>

      {/* Admin Portal Routes */}
      <Route path="/admin" element={<AdminRouteGuard />}>
        <Route element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="businesses" element={<AdminBusinesses />} />
          <Route path="impersonation" element={<AdminImpersonation />} />
          <Route path="system" element={<AdminSystem />} />
          <Route path="lenders" element={<AdminLenders />} />
          <Route path="access-control" element={<AdminAccessControl />} />
          <Route path="audit-log" element={<AdminAuditLog />} />
          <Route path="health" element={<AdminHealth />} />
          <Route path="subscriptions" element={<AdminSubscriptions />} />
          <Route path="api-keys" element={<AdminAPIKeys />} />
        </Route>
      </Route>

      {/* Redirect root to role-appropriate page if authenticated, otherwise to login */}
      <Route
        path="/"
        element={
          user ? (
            <Navigate to={getRoleDefaultRoute()} replace />
          ) : (
            <Navigate to="/auth/login" replace />
          )
        }
      />

      {/* 404 Page */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default App